import { useBreakpointValue, LucideIcon } from '@skin-seoul/design-system';
import { useState } from 'react';
import type { SelectOption } from './common/Select';
import Select from './common/Select';
import { MultipleSelect } from './common/MultipleSelect';
import { useProductService } from '@skin-seoul/service';

interface TagProps {
  label: string;
  value: string;
  onRemove: (value: string) => void;
}

const Tag = ({ label, value, onRemove }: TagProps) => (
  <div className="flex items-center justify-center rounded-[12px] border border-primary-400 bg-primary-50 px-3 py-2 gap-[10px] font-normal transition-colors duration-150 text-paragraph-small">
    <span className="text-primary-400">{label}</span>
    <button
      className="w-4 h-4 bg-transparent text-[#b0b0b8] rounded-full border-none p-0 cursor-pointer flex items-center justify-center"
      aria-label="deselect"
      onClick={() => onRemove(value)}
    >
      <LucideIcon name="X" className="text-primary-400" width="16px" height="16px" />
    </button>
  </div>
);

export const sortOptions: SelectOption[] = [
  { value: 'newest', label: 'Newest' },
  { value: 'oldest', label: 'Oldest' },
  { value: 'highest', label: 'Highest Rating' },
  { value: 'lowest', label: 'Lowest Rating' },
];

interface ReviewFilterBarProps {
  onSortChange: (value: string) => void;
  onSkinTypeChange: (value: string[]) => void;
  onSkinConcernChange: (value: string[]) => void;
}

export const ReviewFilterBar = ({ onSortChange, onSkinTypeChange, onSkinConcernChange }: ReviewFilterBarProps) => {
  const [sort, setSort] = useState<string>('');
  const [skinTypes, setSkinTypes] = useState<string[]>([]);
  const [skinConcerns, setSkinConcerns] = useState<string[]>([]);
  const isMobile = useBreakpointValue({ base: true, tablet: false });
  const { useGetProductTaxonomy } = useProductService();

  const { data: taxonomy } = useGetProductTaxonomy();

  const skinTypeOptions: SelectOption[] =
    taxonomy?.skinType?.map((item) => ({
      label: item.name.split(' ')?.at(0) || '',
      value: item.name.split(' ')?.at(0) || '',
    })) || [];
  const skinConcernOptions: SelectOption[] =
    taxonomy?.skinConcern?.map((item) => ({
      label: item.name,
      value: item.name,
    })) || [];

  const handleSortChange = (value: string) => {
    setSort(value);
    onSortChange(value);
  };
  const handleSkinTypeChange = (values: string[]) => {
    setSkinTypes(values);
    onSkinTypeChange(values);
  };
  const handleSkinConcernChange = (values: string[]) => {
    setSkinConcerns(values);
    onSkinConcernChange(values);
  };

  const renderTags = () => (
    <>
      {sort && (
        <Tag
          key={sort}
          label={sortOptions.find((opt) => opt.value === sort)?.label || ''}
          value={sort}
          onRemove={() => {
            setSort('');
            onSortChange('');
          }}
        />
      )}
      {skinTypeOptions
        .filter((opt) => skinTypes.includes(opt.value))
        .map((opt) => (
          <Tag
            key={opt.value}
            label={opt.label}
            value={opt.value}
            onRemove={(value) => {
              const newValues = skinTypes.filter((v) => v !== value);
              setSkinTypes(newValues);
              onSkinTypeChange(newValues);
            }}
          />
        ))}
      {skinConcernOptions
        .filter((opt) => skinConcerns.includes(opt.value))
        .map((opt) => (
          <Tag
            key={opt.value}
            label={opt.label}
            value={opt.value}
            onRemove={(value) => {
              const newValues = skinConcerns.filter((v) => v !== value);
              setSkinConcerns(newValues);
              onSkinConcernChange(newValues);
            }}
          />
        ))}
    </>
  );

  const renderDropdowns = () => (
    <>
      <Select options={sortOptions} value={sort} onChange={handleSortChange} title="Sort By" />
      <MultipleSelect options={skinTypeOptions} values={skinTypes} onChange={handleSkinTypeChange} title="Skin type" />
      <MultipleSelect
        options={skinConcernOptions}
        values={skinConcerns}
        onChange={handleSkinConcernChange}
        title="Skin concern"
      />
    </>
  );

  if (isMobile) {
    return (
      <div className="flex flex-col items-stretch gap-0">
        <div className="grid grid-cols-3 gap-1 w-full overflow-x-auto">{renderDropdowns()}</div>
        {(sort || skinTypes.length > 0 || skinConcerns.length > 0) && (
          <div className="flex flex-wrap gap-1 mt-[14px]">{renderTags()}</div>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 flex-wrap w-full">
      <div className="grid grid-cols-3 gap-1 w-full">{renderDropdowns()}</div>
      {(sort || skinTypes.length > 0 || skinConcerns.length > 0) && (
        <div className="flex flex-wrap gap-1">{renderTags()}</div>
      )}
    </div>
  );
};

export default ReviewFilterBar;
